import { useState } from 'react';
import { StaafGrafiek } from '../components/charts';
import { stuurTeamsRecap } from '../lib/mail';
import {
  Badge,
  Foutmelding,
  Kaart,
  KaartKop,
  Knop,
  Leeg,
  PaginaKop,
  Paginering,
  SkeletTabel,
  SkeletTegels,
  SkeletVlak,
  Tegel,
  Waarschuwing
} from '../components/ui';
import { SERIES } from '../lib/colors';
import { useAuth } from '../lib/auth';
import { fmtDatumTijd } from '../lib/format';
import { useGebruik, useMailTegoed, useMailVerzending } from '../lib/queries';
import type { GebruikRij } from '../lib/api';

const PERIODES = [
  { dagen: 7, label: 'Laatste 7 dagen' },
  { dagen: 30, label: 'Laatste 30 dagen' },
  { dagen: 90, label: 'Laatste 90 dagen' }
];

export default function Gebruik() {
  const [recapBezig, setRecapBezig] = useState(false);
  const [recapMelding, setRecapMelding] = useState<string | null>(null);
  const [recapFout, setRecapFout] = useState<string | null>(null);

  async function stuurRecap(dagen: number) {
    setRecapBezig(true);
    setRecapMelding(null);
    setRecapFout(null);
    const uit = await stuurTeamsRecap({ dagen });
    if (!uit.ok) setRecapFout(uit.fout ?? 'De recap ging niet weg.');
    else if (!uit.verstuurd) setRecapMelding(uit.reden ?? 'Er viel niets te melden.');
    else setRecapMelding(`Verstuurd: ${uit.potjes} potjes uit ${uit.poules} poule(s).`);
    setRecapBezig(false);
  }

  const { demo } = useAuth();
  const [dagen, setDagen] = useState(30);
  const gebruik = useGebruik(dagen);

  if (demo) {
    return (
      <>
        <PaginaKop titel="Gebruik" />
        <Kaart>
          <Leeg tekst="In demomodus zijn er geen gebruikscijfers; er wordt niets vastgelegd." ikoon="stijging" />
        </Kaart>
      </>
    );
  }

  const rijen = gebruik.data ?? [];
  const totaal = (soort: GebruikRij['soort']) =>
    rijen.filter((r) => r.soort === soort).reduce((n, r) => n + r.aantal, 0);

  const perDag = (soort: GebruikRij['soort']) => {
    const kaart = new Map(rijen.filter((r) => r.soort === soort).map((r) => [r.dag, r.aantal]));
    const uit: { label: string; waarde: number; titel: string }[] = [];
    for (let i = dagen - 1; i >= 0; i--) {
      const d = new Date(Date.now() - i * 86400000);
      const sleutel = d.toISOString().slice(0, 10);
      const aantal = kaart.get(sleutel) ?? 0;
      uit.push({
        label: i === dagen - 1 || i === 0 ? d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' }) : '',
        waarde: aantal,
        titel: `${d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long' })}: ${aantal}`
      });
    }
    return uit;
  };

  const mails = totaal('mail_verstuurd');
  const mislukt = totaal('mail_mislukt');

  return (
    <>
      <PaginaKop
        titel="Gebruik"
        onder="Wat er werkelijk gebeurt in de app"
        rechts={
          <select className="veld w-auto" value={dagen} onChange={(e) => setDagen(Number(e.target.value))}>
            {PERIODES.map((p) => (
              <option key={p.dagen} value={p.dagen}>
                {p.label}
              </option>
            ))}
          </select>
        }
      />

      {gebruik.isLoading ? (
        <>
          <SkeletTegels />
          <div className="mt-3.5 grid gap-3.5 lg:grid-cols-2">
            <SkeletVlak hoogte="h-52" />
            <SkeletVlak hoogte="h-52" />
          </div>
        </>
      ) : (
        <>
          <div className="grid gap-3.5 sm:grid-cols-2 lg:grid-cols-4">
            <Tegel
              label="Verstuurde mails"
              waarde={mails}
              onder={`gemiddeld ${(mails / dagen).toFixed(1)} per dag`}
            />
            <Tegel
              label="Mislukte mails"
              waarde={mislukt}
              onder={mislukt ? 'kijk in het Resend-dashboard' : 'niets misgegaan'}
            />
            <Tegel label="Inlogmomenten" waarde={totaal('inlog')} onder="uit het logboek" />
            <Tegel label="Bewerkingen" waarde={totaal('bewerking')} onder="wijzigingen die ertoe doen" />
          </div>

          <div className="mt-3.5">
            <Kaart>
              <KaartKop
                titel="Teams-recap"
                hint="een samenvatting van de week in het Teams-kanaal"
              />
              <p className="text-sm text-krijt-600">
                Het weekbericht gaat normaal vanzelf de deur uit. Hiermee stuur je hem nu, bijvoorbeeld om te
                kijken of de koppeling staat. Is er in de periode niet gespeeld, dan wordt er niets verstuurd.
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2.5">
                <Knop variant="primair" disabled={recapBezig} onClick={() => void stuurRecap(7)}>
                  {recapBezig ? 'Bezig…' : 'Stuur de recap van deze week'}
                </Knop>
                <Knop disabled={recapBezig} onClick={() => void stuurRecap(30)}>
                  Laatste 30 dagen
                </Knop>
              </div>
              {recapMelding ? <p className="mt-2.5 text-sm text-krijt-600">{recapMelding}</p> : null}
              {recapFout ? (
                <div className="mt-2.5">
                  <Foutmelding>{recapFout}</Foutmelding>
                </div>
              ) : null}
            </Kaart>
          </div>

          <div className="mt-3.5 grid gap-3.5 lg:grid-cols-2">
            <Kaart>
              <KaartKop titel="Mails per dag" hint={mislukt ? `${mislukt} mislukt in deze periode` : undefined} />
              <StaafGrafiek rijen={perDag('mail_verstuurd')} kleur={SERIES[2]} hoogte={170} />
            </Kaart>

            <Kaart>
              <KaartKop titel="Inloggen per dag" />
              <StaafGrafiek rijen={perDag('inlog')} kleur={SERIES[0]} hoogte={170} />
            </Kaart>

            <Kaart>
              <KaartKop titel="Bewerkingen per dag" />
              <StaafGrafiek rijen={perDag('bewerking')} kleur={SERIES[1]} hoogte={170} />
            </Kaart>

          </div>

          <MailPaneel />
        </>
      )}
    </>
  );
}

/**
 * De mailkoppeling, uitgeklapt: één knop om te zien of hij werkt, en daaronder
 * wat er werkelijk de deur uit ging. Mislukte pogingen staan er bij met de
 * melding van Resend erachter — dat is meestal genoeg om te zien wat er scheelt
 * (domein niet geverifieerd, sleutel verlopen, limiet bereikt).
 */
function MailPaneel() {
  /* Ruim ophalen en hier bladeren, net als bij de logs: het zijn er hooguit
     honderden, en zo blijft filteren en bladeren onmiddellijk. */
  const mail = useMailVerzending(300);
  const tegoed = useMailTegoed();
  const [pagina, setPagina] = useState(0);
  const [perPagina, setPerPagina] = useState(25);

  const alles = mail.data ?? [];
  const paginas = Math.max(1, Math.ceil(alles.length / perPagina));
  const huidige = Math.min(pagina, paginas - 1);
  const regels = alles.slice(huidige * perPagina, (huidige + 1) * perPagina);

  const vandaag = tegoed.data?.vandaag ?? 0;
  const limiet = tegoed.data?.limiet ?? 0;
  const vol = Boolean(limiet) && vandaag >= limiet;
  const bijna = Boolean(limiet) && !vol && vandaag >= limiet * 0.8;

  return (
    <div className="mt-3.5">
      <Kaart>
        <KaartKop
          titel="Mail"
          hint={limiet ? `vandaag ${vandaag} van de ${limiet} verstuurd` : 'Alles wat de app zelf verstuurt, via Resend'}
        />

        {/* De grens van Resend. De app telt zelf mee en houdt de honderdenéérste
            tegen, zodat de melding van ons komt en niet uit een foutcode. */}
        {vol ? (
          <Foutmelding>
            <strong>Geblokkeerd: de daglimiet is bereikt.</strong> Er zijn vandaag {vandaag} van de {limiet} mails
            verstuurd, dus nieuwe mail gaat niet weg. Goedkeuren en uitnodigen werkt gewoon door — alleen het bericht
            blijft staan. Morgen telt de teller weer vanaf nul, of verhoog je pakket bij Resend.
          </Foutmelding>
        ) : bijna ? (
          <Waarschuwing>
            Bijna vol: {vandaag} van de {limiet} mails van vandaag zijn op. Daarboven blokkeert de app zelf.
          </Waarschuwing>
        ) : null}

        {mail.isLoading ? (
          <SkeletTabel rijen={4} kolommen={4} />
        ) : regels.length ? (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-y-2 border-krijt-900 bg-krijt-50">
                  {['Moment', 'Aan', 'Onderwerp', 'Uitkomst'].map((k) => (
                    <th key={k} className="opschrift whitespace-nowrap px-3 py-2.5 text-left text-krijt-600">
                      {k}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {regels.map((r) => (
                  <tr key={r.id} className="border-b border-krijt-200">
                    <td className="whitespace-nowrap px-3 py-2 text-krijt-600">{fmtDatumTijd(r.moment)}</td>
                    <td className="px-3 py-2">{r.ontvanger}</td>
                    <td className="px-3 py-2">
                      <span className="block">{r.onderwerp}</span>
                      <span className="block text-xs text-krijt-500">{r.sjabloon}</span>
                    </td>
                    <td className="px-3 py-2">
                      {r.status === 'verstuurd' ? (
                        <Badge toon="goed">verstuurd</Badge>
                      ) : (
                        <span>
                          <Badge toon="slecht">mislukt</Badge>
                          {r.fout ? <span className="ml-2 text-xs text-krijt-600">{r.fout}</span> : null}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Leeg
            tekst="Er is nog geen mail verstuurd. Dat gebeurt pas als je iemand vrijgeeft."
            ikoon="brief"
          />
        )}

        <Paginering
          totaal={alles.length}
          perPagina={perPagina}
          pagina={huidige}
          onPagina={setPagina}
          onPerPagina={(n) => {
            setPerPagina(n);
            setPagina(0);
          }}
        />

        <p className="mt-3 text-xs text-krijt-500">
          De laatste 300 pogingen, geslaagd en mislukt. Geblokkeerde mail staat er ook bij, met de reden erachter.
        </p>
      </Kaart>
    </div>
  );
}
